// frontend/src/pages/ContactUs.js
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next"; // i18next：國際化套件
import { useLocation } from "react-router-dom";
import axios from "../utils/axiosConfig"; // Axios HTTP Client (含 CSRF 設定)
import API_URL from "../config"; // 根據環境設定 API 網址
import { useLoading } from "../contexts/LoadingContext";
import SEO from "../components/SEO";

function ContactUs() {
  const { t } = useTranslation();
  const location = useLocation();
  const { showLoading, hideLoading } = useLoading();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [canonicalUrl, setCanonicalUrl] = useState("");

  useEffect(() => {
    // 在客戶端渲染時設置 canonicalUrl
    if (typeof window !== 'undefined') {
      setCanonicalUrl(window.location.href);
    }
  }, []);

  useEffect(() => {
    if (location.state?.subject) {
      setSubject(location.state.subject);
    }
    if (location.state?.message) {
      setMessage(location.state.message);
    }
  }, [location.state]);

  // 表單提交函式，功能與你提供的版本相同
  const handleSubmit = async (e) => {
    e.preventDefault();
    showLoading(); // 顯示載入動畫
    try {
      const response = await axios.post(
        `${API_URL}/api/contact_us`,
        { name, email, phone, subject, message },
        { withCredentials: true }
      );
      setSuccess(response.data.message);
      setError("");
      // 清空各欄位
      setName("");
      setEmail("");
      setPhone("");
      setSubject("");
      setMessage("");
    } catch (err) {
      console.error("提交聯絡表單時發生錯誤：", err);
      setError(t("contactUs.error.submissionFailed"));
      setSuccess("");
    } finally {
      hideLoading(); // 隱藏載入動畫
    }
  };

  return (
    <>
      <SEO 
        path="/contact"
        title={t("contactUs.title")}
        description={t("contactUs.description")}
        keywords="DamenKYT, contact us, support, help, blockchain security"
        ogTitle={t("contactUs.title")}
        ogDescription={t("contactUs.description")}
        ogImage={`${API_URL}/images/myReportsBanner.png`}
        twitterCard="summary_large_image"
        twitterTitle={t("contactUs.title")}
        twitterDescription={t("contactUs.description")}
        twitterImage={`${API_URL}/images/myReportsBanner.png`}
        canonicalUrl={canonicalUrl || `${API_URL}/contact`}
        robots="index, follow"
        siteName="DamenKYT"
        image={`${API_URL}/images/myReportsBanner.png`}
        imageWidth="1200"
        imageHeight="630"
        imageAlt={t("contactUs.title")}
      />
      <div className="min-h-screen bg-white dark:bg-gray-900">
        {/* Hero Banner 區塊 */}
        <div
          className="w-full h-64 bg-cover bg-center flex flex-col items-center justify-center text-white"
          style={{ backgroundImage: `url('/images/myReportsBanner.png')` }}
        >
          <div className="text-3xl font-bold">{t("contactUs.title")}</div>
          <p className="text-lg font-medium mt-2">{t("contactUs.contactPrompt")}</p>
        </div>

        {/* 主內容區，使用 container 置中並設置適當間距 */}
        <div className="container mx-auto px-4 py-8">
          {/* 標題與描述 */}
          <div className="text-center mb-8">
            <p className="mt-4 text-sm text-black dark:text-white">
              {t("contactUs.description")}
            </p>
          </div>

          {/* 顯示錯誤或成功訊息 */}
          {error && <div className="mb-4 text-center text-red-500">{error}</div>}
          {success && <div className="mb-4 text-center text-green-600">{success}</div>}

          {/* 表單內容 */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Row 1：姓名與 Email，使用 grid 排版 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block mb-2 text-black dark:text-white">
                  {t("contactUs.nameLabel")}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("contactUs.enterName")}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block mb-2 text-black dark:text-white">
                  {t("contactUs.emailLabel")}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("contactUs.enterEmail")}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none"
                  required
                />
              </div>
            </div>

            {/* Row 2：電話與主旨 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block mb-2 text-black dark:text-white">
                  {t("contactUs.phoneLabel")}
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t("contactUs.enterPhone")}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none"
                />
              </div>
              <div>
                <label className="block mb-2 text-black dark:text-white">
                  {t("contactUs.subjectLabel")}
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t("contactUs.enterSubject")}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none"
                  required
                />
              </div>
            </div>

            {/* Row 3：內容 */}
            <div>
              <label className="block mb-2 text-black dark:text-white">
                {t("contactUs.contentLabel")}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("contactUs.enterMessage")}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none resize-y h-32"
                required
              />
            </div>

            {/* Row 4：送出按鈕 */}
            <div className="text-center">
              <button
                type="submit"
                className="w-full md:w-auto bg-black text-white py-3 px-8 rounded-full hover:bg-gray-800 transition duration-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                {t("contactUs.submit")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default ContactUs;
